import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    StudentPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FormSelectChoice,
    getAvailableLocaleChoices,
    StudentPersonalInfoResponse,
    hasFailedInstantMatch,
    ChangeLocaleComponent,
    Organization,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    CollegeNameComponent,
    FirstNameComponent,
    LastNameComponent,
    BirthDateComponent,
    EmailComponent,
    FormFooterComponent,
    Locale,
    CountryComponentWrapper,
    getSafe,
    PhoneNumberComponent as PhoneNumber,
    PoweredByComponent,
} from '@sheerid/jslib';
import { PlanOfUseComponent } from '../FormFields/PlanOfUse/PlanOfUseComponent';
import { PersonalEmailComponent } from '../FormFields/PersonalEmail/PersonalEmailComponent';
import { programId } from '../../programOptions';
import { RegionSelectComponent } from '../FormFields/RegionSelectComponent';

interface StepStudentPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
            if (draft.countryChoice) {
                draft.metadata = {
                    ...draft.metadata,
                    country: draft.countryChoice.value,
                };
            }
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            // @ts-ignore
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    return (
        <div id="sid-step-student-personal-info" className="sid-l-container">
            {
                failedInstantMatch
                    ? (
                        <div className="sid-header">
                            <div className="sid-header__title sid-l-horz-center">
                                <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                            </div>

                            <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                                <FormattedHTMLMessage
                                    id="step.personalInfo.tryAgain.subtitle"
                                    defaultMessage="Your full name and birthdate must match what is on your official school records."
                                />
                            </div>
                        </div>
                    )
                    : (
                        <div className="sid-header">
                            <div className="sid-header__title sid-l-horz-center">
                                <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Student-Only Offer" />
                            </div>

                            <div className="sid-header__subtitle">
                                <FormattedHTMLMessage
                                    id="step.personalInfo.subtitle"
                                    defaultMessage="Tell us about yourself below."
                                />
                                &nbsp;
                                <div className="sid-field__label sid-header__subtitle2">
                                    <FormattedHTMLMessage id="step.personalInfo.subtitle2" defaultMessage="*This is only for students 16 and older. If you are under the age of 16, please have your parent fill out the form." />
                                </div>
                            </div>
                        </div>
                    )
            }

            {
                (localeChoices.length > 1)
                    ? (
                        <ChangeLocaleComponent
                            options={localeChoices}
                            value={viewModel.localeChoice || defaultLocaleChoice}
                            isErrored={false}
                            onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
                        />
                    ) : null
            }

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="organization"
                viewModelDraftDecorator={(draft, countryChoice) => {
                    const viewModelDraft = draft as StudentPersonalInfoViewModel;
                    const country = countryChoice ? countryChoice.value : undefined;

                    if (viewModel.countryChoice?.value !== country) {
                        viewModelDraft.metadata.region = '';
                    }
                }}
            />

            <RegionSelectComponent
                verificationService={verificationService}
                country={viewModel.countryChoice?.value}
                value={viewModel.metadata?.region}
                onChange={
                    (newValue: FormSelectChoice<string, string>) => {
                        updateStudentViewModel('metadata', {
                            ...verificationService.viewModel.metadata,
                            region: newValue?.value.toString() || '',
                        });
                        updateFieldValidationErrorsByFieldId('region', newValue?.value, verificationService);
                    }
                }
                isRequired
                isErrored={!!fieldValidationErrors.region}
            />

            <div className="sid-field__label">
                <FormattedHTMLMessage id="nameTitle" defaultMessage="Enter your name as it appears on your school records." />
            </div>

            <FirstNameComponent
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                placeholder={
                    intl.formatMessage({
                        id: 'firstName',
                        defaultMessage: 'Legal First Name',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastNameComponent
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                placeholder={
                    intl.formatMessage({
                        id: 'lastName',
                        defaultMessage: 'Legal Last Name',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <div className="sid-email-title sid-field__label">
                <FormattedHTMLMessage id="emailTitle" defaultMessage="For quicker verification, please use your university email address." />
            </div>

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={
                    intl.formatMessage({
                        id: 'email',
                        defaultMessage: 'School Email Address',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <PersonalEmailComponent
                value={getSafe(() => viewModel.metadata['personal-email'], '')}
                isErrored={!!fieldValidationErrors['personal-email']}
                placeholder={
                    intl.formatMessage({
                        id: 'personalEmail',
                        defaultMessage: 'Personal Email Address (optional)',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, 'personal-email': newValue });
                        updateFieldValidationErrorsByFieldId('personal-email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled
                    ? (
                        <PhoneNumber
                            isRequired={!!verificationService.programTheme.smsLoopEnabled}
                            value={viewModel.phoneNumber}
                            isErrored={!!fieldValidationErrors.phoneNumber}
                            internationalSupport
                            onChange={
                                (newValue: string) => {
                                    updateStudentViewModel('phoneNumber', newValue);
                                    updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                                }
                            }
                        />
                    )
                    : null
            }

            <CollegeNameComponent
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                placeholder={
                    intl.formatMessage({
                        id: 'schoolName',
                        defaultMessage: 'Name of College/University',
                    })
                }
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <BirthDateComponent
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <PlanOfUseComponent
                value={getSafe(() => viewModel.metadata['plan-of-use'], '')}
                isErrored={!!fieldValidationErrors['plan-of-use']}
                placeholder={
                    intl.formatMessage({
                        id: 'planOfUse',
                        defaultMessage: 'Plan of Use',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, 'plan-of-use': newValue });
                        updateFieldValidationErrorsByFieldId('plan-of-use', newValue, verificationService);
                    }
                }
            />

            <div className="sid-field__label">
                <FormattedHTMLMessage
                    id="step.personalInfo.licenseAgreement"
                    defaultMessage='All licenses provided under an Alteryx Academy program are governed by the Alteryx <a class="sid-link" target="_blank" href="https://www.alteryx.com/sites/default/files/2019-10/Alteryx-EULA-v-2019-03.pdf">End User License Agreement</a> and the <a class="sid-link" target="_blank" href="http://www.alteryx.com/privacy">Alteryx Privacy Policy</a>, as well as any additional terms of use outlined within each product, service, or site.'
                />
            </div>

            <div className="sid-field__label">
                <FormattedHTMLMessage
                    id="optInFutureCommunication"
                    defaultMessage="By submitting this form, I am opting in to receive future communications from SparkED education program."
                />
            </div>

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        {
                            failedInstantMatch
                                ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                                : <FormattedHTMLMessage id="verifyMyStudentStatus" defaultMessage="Verify My Student Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="cantFindSchool"
                    defaultMessage="If your school is not in the list, please contact SheerID directly. Please send the <a class='sid-link' href='https://offers.sheerid.com/sheerid/add-school-request/?pid={pid}'>request form</a> after filling in the necessary items. If your school is added in the list, you will get the email notification from SheerID."
                    values={{
                        pid: programId,
                    }}
                />
                {
                    (viewModel.countryChoice && viewModel.countryChoice.value !== 'US')
                        ? (
                            <FormattedHTMLMessage
                                id="informationTransferredToUS"
                                defaultMessage="Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy."
                            />
                        ) : null
                }
                <div className="sid-footer sid-l-space-top-md">
                    <FormattedHTMLMessage
                        id="SparkEDFaq"
                        defaultMessage="Please visit our <a href=https://s3.us-east-2.amazonaws.com/alteryx.sparked/Documentation/SparkED+Getting+Started+FAQ.pdf target=“_blank”>SparkED: Getting Started FAQ</a> to learn about receiving and activating your Alteryx license key."
                    />
                </div>
            </FormFooterComponent>

            <PoweredByComponent verificationService={verificationService} />
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
